// src/components/PassUsageModal.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  TicketIcon,
  CreditCardIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const PassUsageModal = ({ 
  isOpen, 
  onClose, 
  onUsePass, 
  onBuySubscription,
  passCount,
  eventTitle,
  eventDate,
  eventSlug,
  loading 
}) => {
  const [selectedOption, setSelectedOption] = useState('use_pass');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedOption === 'use_pass') {
      onUsePass();
    } else {
      onBuySubscription();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className="bg-green-500 rounded-full p-2 mr-3">
                      <TicketIcon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900  mb-2 pt-2">
                      You Have {passCount} Pass{passCount > 1 ? 'es' : ''}!
                    </h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-green-800 text-sm">
                  Great news! You can use one of your passes for this event.
                </p>
              </div>

              {/* Event Details */}
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                  Event Details
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <CalendarDaysIcon className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900">{eventTitle}</p>
                      <p className="text-sm text-gray-600">{eventDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Choose Your Option:
                </h3>

                {/* Option 1: Use Pass */}
                <button
                  onClick={() => setSelectedOption('use_pass')}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    selectedOption === 'use_pass'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      selectedOption === 'use_pass'
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedOption === 'use_pass' && (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">Use My Pass</h4>
                        <span className="text-green-600 font-bold">FREE</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Use one of your {passCount} available pass{passCount > 1 ? 'es' : ''} for this event
                      </p>
                      {passCount > 1 && (
                        <p className="text-xs text-gray-500 mt-2">
                          ✓ You'll have {passCount - 1} pass{passCount - 1 > 1 ? 'es' : ''} remaining after this
                        </p>
                      )}
                    </div>
                  </div>
                </button>

                {/* Option 2: Buy Subscription */}
                <button
                  onClick={() => setSelectedOption('buy_subscription')}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    selectedOption === 'buy_subscription'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      selectedOption === 'buy_subscription'
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedOption === 'buy_subscription' && (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">Subscribe Instead</h4>
                        <span className="text-purple-600 font-bold">¥2,100/mo</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Get unlimited access to all events + keep your {passCount} pass{passCount > 1 ? 'es' : ''}
                      </p>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-500">✓ Weekly Signature Fika</p>
                        <p className="text-xs text-gray-500">✓ Monthly gatherings & Curated Fika</p>
                        <p className="text-xs text-gray-500">✓ Save vs. buying individual passes</p>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Actions */}
              <div className="p-6 bg-gray-50 border-t border-gray-200">
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 ${
                      selectedOption === 'use_pass'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      selectedOption === 'use_pass' ? 'Confirm & Use Pass' : 'Go to Subscription'
                    )}
                  </button>
                </div>

                {selectedOption === 'use_pass' && (
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    This will mark one pass as used for this event
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PassUsageModal;